import * as XLSX from 'xlsx';

export interface ExcelSupplier {
  'Supplier ID': string;
  'Display Name': string;
  'Email': string;
  'Tax ID': string;
  'Payment Terms': number;
  'Is Active': boolean | string; // Can be boolean or string from Excel
  'Created At': string;
  'Updated At': string;
  'Created By': string;
  'Org ID': string;
}

export function exportSuppliersToExcel(suppliers: any[], filename: string = 'suppliers.xlsx') {
  // Transform suppliers data to Excel format with ID as first column
  const excelData: ExcelSupplier[] = suppliers.map(supplier => ({
    'Supplier ID': supplier.id || '',
    'Display Name': supplier.display_name || '',
    'Email': supplier.email || '',
    'Tax ID': supplier.tax_id || '',
    'Payment Terms': supplier.payment_terms || 30,
    'Is Active': supplier.is_active !== false, // Default to true if undefined
    'Created At': supplier.created_at || '',
    'Updated At': supplier.updated_at || '',
    'Created By': supplier.created_by || '',
    'Org ID': supplier.org_id || ''
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Auto-size columns
  const columnWidths = [
    { wch: 40 }, // Supplier ID
    { wch: 30 }, // Display Name
    { wch: 30 }, // Email
    { wch: 20 }, // Tax ID
    { wch: 15 }, // Payment Terms
    { wch: 12 }, // Is Active
    { wch: 20 }, // Created At
    { wch: 20 }, // Updated At
    { wch: 40 }, // Created By
    { wch: 40 }  // Org ID
  ];
  worksheet['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Suppliers');

  // Write file
  XLSX.writeFile(workbook, filename);
}

export function parseExcelFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);
        
        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}

export function convertExcelToSupplierData(excelData: ExcelSupplier[]) {
  return excelData.map(row => {
    const isActiveValue = row['Is Active'];
    let isActive = true; // Default to true
    
    // Handle various representations of false
    if (isActiveValue === false || 
        isActiveValue === 'false' || 
        isActiveValue === 'FALSE' || 
        isActiveValue === 'False' ||
        String(isActiveValue) === '0') {
      isActive = false;
    }
    
    return {
      id: row['Supplier ID'] || undefined, // undefined for new suppliers
      display_name: row['Display Name'],
      email: row['Email'] || null,
      tax_id: row['Tax ID'] || null,
      payment_terms: typeof row['Payment Terms'] === 'number' ? row['Payment Terms'] : 30,
      is_active: isActive
    };
  });
}

// Bills-related interfaces and functions
export interface ExcelBill {
  'Bill ID': string;
  'Bill Number': string;
  'Title': string;
  'Supplier ID': string;
  'Supplier Name': string;
  'Amount': number;
  'Grand Total': number;
  'Currency': string;
  'Status': string;
  'Issue Date': string;
  'Due Date': string;
  'Description': string;
  'Created At': string;
  'Updated At': string;
  'Org ID': string;
}

export function exportBillsToExcel(bills: any[], filename: string = 'bills.xlsx') {
  // Transform bills data to Excel format with ID as first column
  const excelData: ExcelBill[] = bills.map(bill => ({
    'Bill ID': bill.id || '',
    'Bill Number': bill.bill_number || '',
    'Title': bill.title || '',
    'Supplier ID': bill.supplier_id || '',
    'Supplier Name': bill.suppliers?.display_name || '',
    'Amount': bill.amount || 0,
    'Grand Total': bill.grand_total || bill.amount || 0,
    'Currency': bill.currency || 'EUR',
    'Status': bill.status || 'draft',
    'Issue Date': bill.issue_date || '',
    'Due Date': bill.due_date || '',
    'Description': bill.description || '',
    'Created At': bill.created_at || '',
    'Updated At': bill.updated_at || '',
    'Org ID': bill.org_id || ''
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Auto-size columns
  const columnWidths = [
    { wch: 40 }, // Bill ID
    { wch: 20 }, // Bill Number
    { wch: 30 }, // Title
    { wch: 40 }, // Supplier ID
    { wch: 30 }, // Supplier Name
    { wch: 15 }, // Amount
    { wch: 15 }, // Grand Total
    { wch: 10 }, // Currency
    { wch: 15 }, // Status
    { wch: 15 }, // Issue Date
    { wch: 15 }, // Due Date
    { wch: 40 }, // Description
    { wch: 20 }, // Created At
    { wch: 20 }, // Updated At
    { wch: 40 }  // Org ID
  ];
  worksheet['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Bills');

  // Write file
  XLSX.writeFile(workbook, filename);
}

export function convertExcelToBillData(excelData: ExcelBill[]) {
  return excelData.map(row => {
    return {
      id: row['Bill ID'] || undefined, // undefined for new bills
      bill_number: row['Bill Number'] || null,
      title: row['Title'] || null,
      supplier_id: row['Supplier ID'] || null,
      amount: typeof row['Amount'] === 'number' ? row['Amount'] : 0,
      grand_total: typeof row['Grand Total'] === 'number' ? row['Grand Total'] : row['Amount'] || 0,
      currency: row['Currency'] || 'EUR',
      status: row['Status'] || 'draft',
      issue_date: row['Issue Date'] || null,
      due_date: row['Due Date'] || null,
      description: row['Description'] || null
    };
  });
}